import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "./axiosConfig";
import Modal from "./components/Modal";
import "./GroupManagement.css"; // Import new CSS file

function GroupManagement() {
  const [groups, setGroups] = useState([]);
  const [searchString, setSearchString] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize] = useState(10);  
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const navigate = useNavigate();    
  const token = localStorage.getItem("accessToken");

  const fetchGroups = useCallback(
    async (page) => {
      try {
        const response = await api.post(
          "/group/list",
          {
            searchString: searchString,
            skip: (page - 1) * pageSize,
            take: pageSize,
          },
          { headers: { Authorization: token } }
        );

        const data = response.data.data;
        if (data) {
          setGroups(data);
          setTotalPages(response.data.pagination.totalPages);
          setCurrentPage(response.data.pagination.currentPage);
        } else {
          console.error("Failed to fetch groups:", data.message);
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
      }
    },
    [searchString, pageSize, token]
  );
  
  useEffect(() => {
    fetchGroups(currentPage);
  }, [currentPage, searchString, fetchGroups]);

  const handleSearchChange = (e) => {
    setSearchString(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = async (data) => {
      try {
        await api.patch(`/group/${data.id}/soft-delete`, {
            headers: { Authorization: token },
        });
        setIsDeleteModalOpen(false); 
        fetchGroups(currentPage);
      } catch (error) {
        console.error("Error fetching group:", error);
        alert("Failed to load user data. Please try again.");
      };
  };

  const handleEdit = async (data) => {
    try {
        await api.get(`/group/${data.id}`, {
        headers: { Authorization: token },
      });
      setSelectedGroup(data)
      setIsEditModalOpen(true);
    } catch (error) {
      console.error("Error fetching group:", error);
      alert("Failed to load group data. Please try again.");
    };
  }

  const handleSave = async (data) => {
    console.log(data);
      try {
          await api.patch(`/group/${data.id}`, data, {
          headers: { Authorization: token },
          });
          setIsEditModalOpen(false);
          fetchGroups(currentPage);
      } catch (error) {
          console.error("Error updating group:", error);
      }
  };

  const handleCreate = async (data) => {
    console.log(data)
    try {
      await api.post(`/group`, data, {
      headers: { Authorization: token },
      });
      setIsCreateModalOpen(false);
      fetchGroups(currentPage);
    } catch (error) {
        console.error("Error updating group:", error);
    }
  };

    const handleBack = () => {
      navigate("/admin");
    };

    const handleDeleteClick = (user) => {
      setSelectedGroup(user);
      setIsDeleteModalOpen(true);
    };

  return (
    <div className="user-management-container">
      <button className="back-button" onClick={handleBack}>
        Back
      </button>

      <div className="header-title">
        <h2>Group Management</h2>
        <button
          className="user-list-button"
          onClick={() => setIsCreateModalOpen(true)}
        >
          Create Group
        </button>
      </div>

      <div className="search-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Search groups..."
          value={searchString}
          onChange={handleSearchChange}
        />
      </div>

      {groups.length > 0 ? 
      <>
      <table className="group-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {groups.map((group) => (
            <tr key={group.id}>
              <td>{group.name}</td>
              <td>{group.is_deleted ? "Inactive" : "Active"}</td>
              <td className="actions">
                <button className="edit-button" onClick={() => handleEdit(group)}>
                  Edit
                </button>
                <button className={group.is_deleted ? "activate-button" : "delete-button"} onClick={() => handleDeleteClick(group)}>
                  {group.is_deleted ? "Activate" : "Deactivate"}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table> 
      
      <div className="pagination">
      <button
        className="pagination-button"
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
      >
        Previous
      </button>
      <span>Page {currentPage} of {totalPages}</span>
      <button
        className="pagination-button"
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages}
      >
        Next
      </button>
    </div>
    </>: "No Groups Data Available"}

    <Modal
      isOpen={isCreateModalOpen}
      onClose={() => {
        setIsCreateModalOpen(false)
        setSelectedGroup(null)
      }}
      title="Create New Group"
      fields={[
        { name: "name", label: "Group Name", required: true },
        { name: "description", label: "Description", value: "" }
      ]}
      onSubmit={(data) => handleCreate(data)}
      mode="create"
    />

    <Modal
      isOpen={isEditModalOpen}
      onClose={() => setIsEditModalOpen(false)}
      title="Edit Group"
      fields={[
        { name: "id", label: "ID", value: selectedGroup?.id, hidden: true },
        { name: "name", label: "Group Name", value: selectedGroup?.name },
        { name: "description", label: "Description", value: selectedGroup?.description }
      ]}
      onSubmit={(data) => handleSave(data)}
      mode="edit"
    />

    <Modal
      isOpen={isDeleteModalOpen}
      onClose={() => setIsDeleteModalOpen(false)}
      title="Delete Group"
      message="Are you sure to delete this Group"
      fields={[
        { name: "id", label: "ID", value: selectedGroup?.id, hidden: true },
        { name: "name", label: "Group Name", value: selectedGroup?.name },
        { name: "is_deleted", label: "IsDeleted", value: selectedGroup?.is_deleted, hidden: true }
      ]}
      onSubmit={(data) => handleDelete(data)}
      mode="delete"
      />
    </div>
  );
};

export default GroupManagement;
